import { ApiEnvelope, ErrorEntry } from '../models';

export class ContentModerationApiError extends Error {
  public readonly code: string;
  public readonly param: string;
  public readonly httpStatus: number;
  public readonly raw: unknown;

  constructor(opts: { code: string; param: string; httpStatus: number; raw?: unknown; message?: string }) {
    super(opts.message ?? `${opts.code}${opts.param ? ` (${opts.param})` : ''}`);
    this.name = 'ContentModerationApiError';
    this.code = opts.code;
    this.param = opts.param;
    this.httpStatus = opts.httpStatus;
    this.raw = opts.raw;
  }
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function firstError(errors: ErrorEntry[] | undefined): ErrorEntry {
  if (Array.isArray(errors) && errors.length > 0) return errors[0];
  return { error: 'envelope.malformed', param: '' };
}

function isEnvelope<T>(body: unknown): body is ApiEnvelope<T> {
  return Boolean(body && typeof body === 'object' && 'success' in (body as Record<string, unknown>));
}

export async function unwrapEnvelope<T>(response: Response): Promise<T> {
  const body = await readJson(response);

  if (!isEnvelope<T>(body)) {
    throw new ContentModerationApiError({
      code: response.ok ? 'envelope.malformed' : 'http.error',
      param: '',
      httpStatus: response.status,
      raw: body,
    });
  }

  if (!body.success) {
    const err = firstError(body.error);
    throw new ContentModerationApiError({
      code: err.error,
      param: err.param,
      httpStatus: response.status,
      raw: body,
    });
  }

  return body.data;
}

export interface EnvelopeWithWarnings<T> {
  data: T;
  warnings: ErrorEntry[];
}

export async function unwrapEnvelopeWithWarnings<T>(response: Response): Promise<EnvelopeWithWarnings<T>> {
  const body = await readJson(response);

  if (!isEnvelope<T>(body)) {
    throw new ContentModerationApiError({
      code: response.ok ? 'envelope.malformed' : 'http.error',
      param: '',
      httpStatus: response.status,
      raw: body,
    });
  }

  if (!body.success) {
    const err = firstError(body.error);
    throw new ContentModerationApiError({
      code: err.error,
      param: err.param,
      httpStatus: response.status,
      raw: body,
    });
  }

  return { data: body.data, warnings: body.error ?? [] };
}
